import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, IconButton } from "@mui/material";
import { ModeEditOutlineTwoTone, InfoOutlined, Add } from "@mui/icons-material";

import { useAuth, useTablePagination } from "hooks";

import HighlightedText from "components/Shared/HighlightedText";
import OpenInNewTab from "components/Shared/OpenInNewTab";

import { getLedgerSourceInfo } from "utils/accountLedgerformatValue";

import { formatAmount } from "utils/formatString";

import AppTableDateTime from "components/Shared/AppTableDateTime";

import { useLazyListAccountLedgerQuery } from "store/services/accountLedgerApi";
import { BaseTabs } from "components/Shared/BaseTabs";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import AccountLedgerTableFilter from "./accountLedgerComponents/AccountLedgerTableFilter";
const breadcrumbs = [{ title: "Account Ledger" }];

const accountLedgerListTabs = [
  { label: "Active", value: "Active" },
  { label: "Inactive", value: "Inactive" },
];
const AccountLedgerListScreen = () => {
  const { isEntityAdmin } = useAuth();

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";

  const keyword = searchParams.get("search") || "";

  const sortBy = searchParams.get("sortBy") || "createdAt";

  const sortDirection = searchParams.get("sortDirection") || -1;

  const columns = [
    {
      name: "Payment Account",
      selector: (row) => row?.paymentAccount?.accountName,

      cell: (row) => (
        <Link
          to={`/app/crm/account-ledger/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-500 hover:underline"
        >
          {row?.paymentAccount?.accountName ? (
            <HighlightedText
              text={`${row?.paymentAccount?.accountName}`}
              highlight={keyword}
            />
          ) : (
            "N/A"
          )}
        </Link>
      ),
      grow: 1.5,
    },
    ...(!isEntityAdmin
      ? [
          {
            name: "Entity",
            cell: (row) => (
              <OpenInNewTab
                label={row?.entity?.displayName ?? row?.entity?.name}
                link={`/app/crm/entity/${row?.entity?._id}/detail`}
                disableIcon
              />
            ),
          },
        ]
      : []),
    {
      name: "Party",
      selector: (row) => {
        const { vendor, contractor, customer } = row;

        if (vendor) {
          return (
            <OpenInNewTab
              label={
                <>
                  {vendor?.vendor?.name}
                  <div style={{ fontSize: "0.75rem", color: "darkgray" }}>
                    Vendor Profile
                  </div>
                </>
              }
              link={`/app/crm/vendor-profile/${vendor?._id}/detail`}
              disableIcon
            />
          );
        }

        if (contractor) {
          return (
            <OpenInNewTab
              label={
                <>
                  {contractor?.name}
                  <div style={{ fontSize: "0.75rem", color: "darkgray" }}>
                    Contractor Profile
                  </div>
                </>
              }
              link={`/app/crm/contractor/${contractor?._id}/detail`}
              disableIcon
            />
          );
        }

        if (customer) {
          return (
            <OpenInNewTab
              label={
                <>
                  {customer?.customerUser?.name}
                  <div style={{ fontSize: "0.75rem", color: "darkgray" }}>
                    Customer Profile
                  </div>
                </>
              }
              link={`/app/crm/customerProfile/${customer?._id}/detail`}
              disableIcon
            />
          );
        }

        return "N/A";
      },
      align: "center",
    },
    {
      name: "Original Source",
      selector: (row) => {
        const sourceInfo = getLedgerSourceInfo(
          row?.originalSource,
          row?.originalSourceRef
        );
        return (
          <div className="">
            {" "}
            <OpenInNewTab
              label={sourceInfo?.label}
              link={`/app/crm/${sourceInfo?.link}/${sourceInfo?._id}/detail`}
              disableIcon
            />
          </div>
        );
      },
      align: "center",
    },

    {
      name: "Added By",
      selector: (row) => (
        <OpenInNewTab
          label={row?.addedBy?.name}
          link={`/app/crm/user/${row?.addedBy?._id}/detail`}
          disableIcon
        />
      ),
      align: "center",
    },

    {
      name: "Amount",
      selector: (row) =>
        `${row?.entity?.currency?.code} ${formatAmount(row?.amount)}`,
      align: "center",
    },
    {
      name: "Transaction Date",
      selector: (row) =>
        <AppTableDateTime date={row?.transactionDate} dateOnly /> ?? "N/A",
      align: "center",
    },

    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/account-ledger/${row._id}/detail`}>
            <IconButton color="primary" aria-label="Edit Payment Account">
              <InfoOutlined />
            </IconButton>
          </Link>

          <Link to={`/app/crm/account-ledger/${row._id}/edit`}>
            <IconButton size="small" aria-label="Edit Payment Account">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
      maxWidth: "100px",
    },
  ];
  // RTK Query
  const [
    listAccountLedger,
    {
      data: accountLedgerRes,
      isLoading: loadingAccountLedger,
      isFetching: fetchingAccountLedger,
      error,
    },
  ] = useLazyListAccountLedgerQuery();

  const isBusy = loadingAccountLedger || fetchingAccountLedger;

  React.useEffect(() => {
    listAccountLedger({
      page: currentPage,
      limit: perPage,
      sortBy,
      sortDirection,
      ...(keyword && { keyword }),
      status: activeTab === "Active" ? "Active" : "Inactive",
    });
  }, [
    activeTab,
    currentPage,
    keyword,
    listAccountLedger,
    perPage,
    sortBy,
    sortDirection,
  ]);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <BaseTabs
          tabs={accountLedgerListTabs}
          defaultValue={activeTab}
          // actions={<ExportAccountLedgerExcel />}
        />

        <AccountLedgerTableFilter />

        <AppTable
          columns={columns}
          data={accountLedgerRes?.data ?? []}
          progressPending={isBusy}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={accountLedgerRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default AccountLedgerListScreen;

const PageActions = () => {
  return (
    <Link to="/app/crm/account-ledger/create">
      <Button startIcon={<Add />} variant="contained">
        New Account Ledger
      </Button>
    </Link>
  );
};
